import { Logger } from 'utilities/logger';

/** Wording for the action that empties a cap table of every security. */
export const CAP_TABLE_CLEAR_COPY = {
  menuLabel: 'Clear entire cap table',
  title: 'Clear entire cap table?',
  confirmLabel: 'Clear cap table',
  confirmPhrase: 'CLEAR',
  confirmPrompt: 'Type CLEAR to confirm',
  points: [
    'Every security on this cap table is deleted, with its values, AI suggestions, citations, and Excel references.',
    'Securities used by fund ownership or a valuation block the clear; unlink them first.',
    'Other measurement dates and the documents referenced here are not affected.',
    'This cannot be undone.',
  ],
} as const;

export interface CapTableClearResult {
  securities_deleted?: number;
}

export interface CapTableClearDeps {
  clearCapTable: (capTableId: number) => Promise<CapTableClearResult>;
  refresh: () => Promise<unknown> | unknown;
  notify: (message: string, variant: 'success' | 'error') => void;
  /** Names the table in the success toast, so a slow refresh cannot make it ambiguous. */
  targetLabel?: string;
  /** False once the analyst has moved to another cap table, so a late reply is dropped. */
  isStillSelected?: (capTableId: number) => boolean;
}

type ClearErrorBody = { detail?: unknown; blocked_securities?: unknown };

type ClearErrorLike = { body?: ClearErrorBody; response?: { body?: ClearErrorBody } };

/** The server's reason for refusing a clear, naming the securities that blocked it. */
const refusalReason = (error: unknown): string | null => {
  const { body, response } = (error ?? {}) as ClearErrorLike;
  const payload = response?.body ?? body ?? {};
  const detail = typeof payload.detail === 'string' ? payload.detail.trim() : '';
  const blocked = (Array.isArray(payload.blocked_securities) ? payload.blocked_securities : []).filter(
    (name): name is string => typeof name === 'string' && name.length > 0 && !detail.includes(name)
  );

  const message = [detail, blocked.length > 0 ? `In use: ${blocked.join(', ')}.` : ''].filter(Boolean).join(' ');
  return message || null;
};

/** Empties the cap table, reloads the grid, and tells the analyst how it went. */
export const runCapTableClear = async (
  capTableId: number,
  { clearCapTable, refresh, notify, targetLabel = '', isStillSelected = () => true }: CapTableClearDeps
): Promise<boolean> => {
  try {
    const result = await clearCapTable(capTableId);
    if (!isStillSelected(capTableId)) {
      return true;
    }
    await refresh();
    if (!isStillSelected(capTableId)) {
      return true;
    }
    const cleared = `Cleared ${result?.securities_deleted ?? 0} securities`;
    notify(targetLabel ? `${cleared} from ${targetLabel}` : cleared, 'success');
    return true;
  } catch (error) {
    Logger.error(`capTableClear - clearing cap table ${capTableId} failed`, error);
    if (isStillSelected(capTableId)) {
      notify(refusalReason(error) ?? 'Could not clear the cap table', 'error');
    }
    return false;
  }
};
